<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-4">
      <h3 class="mb-0">Business plan</h3>
      <div>
        <button v-if="isEditing" class="btn btn-secondary mr-3" @click="cancel" :disabled="isSaving">Annulla</button>
        <button v-if="isEditing" class="btn btn-primary" @click="confirmSave" :disabled="isSaving">
          <span v-if="isSaving" class="mr-2">
            <div class="spinner-border text-light" style="height: 1rem; width: 1rem; border-width: 0.15em" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </span>
          Salva
        </button>
        <button v-if="!isEditing" class="btn btn-outline-primary" @click="edit">Modifica</button>
      </div>
    </div>
    <div v-if="businessPlan" class="row">
      <!-- BUY -->
      <div class="col-md-6">
        <!-- buy categories -->
        <div v-for="cat in businessPlan.buy" :key="cat.key" class="card mb-4">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h3 class="mb-0">{{ cat.label }}</h3>
              <button class="btn btn-xs btn-icon btn-ghost-primary" @click="toggleCat(cat.key)">
                <i v-if="!displayCat[cat.key]" class="tio-add"></i>
                <i v-else class="tio-remove"></i>
              </button>
            </div>

            <div v-show="displayCat[cat.key]">
              <div v-for="field in cat.fields" :key="field.id" class="row no-gutters py-1 border-bottom">
                <div class="col-7">{{ field.field.label }}</div>
                <div class="col-2 pr-3">
                  <div v-if="checkFieldComputedSecondary(field)" class="d-flex">
                    <span v-if="!isEditing">{{ numf(getFieldValue(field)) }}</span>
                    <input v-else type="number" class="form-control bp-input" v-model="field.custom_value" :placeholder="numf(getFieldValue(field))" @input="setFieldAsEdited(field)" />
                    <span v-if="field.field.computed.operator == '%'">%</span>
                  </div>
                </div>
                <div class="col-3">
                  <div class="d-flex justify-content-end">
                    <span class="mr-1">{{ field.field.unit }}</span>
                    <div v-if="checkFieldComputedSecondary(field)">
                      <span>{{ numf(getComputedSecondaryValue(field)) }}</span>
                    </div>
                    <div v-else>
                      <span v-if="!isEditing">{{ numf(getFieldValue(field)) }}</span>
                      <!-- <input v-else type="number" class="form-control bp-input" v-model="field.custom_value" :placeholder="numf(getFieldValue(field))" @input="setFieldAsEdited(field)" /> -->
                      <currency-input v-else short v-model="field.custom_value" :placeholder="numf(getFieldValue(field))" @input="setFieldAsEdited(field)" />
                    </div>
                    <button
                      type="button"
                      @click="field.custom_value = null"
                      data-toggle="tooltip"
                      data-html="true"
                      data-placement="left"
                      title="Ripristina valore di default"
                      class="btn btn-outline-secondary p-1 bp-reset-btn"
                      v-show="isEditing && field.custom_value != null"
                    >
                      <i class="tio-refresh"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="row no-gutters">
              <div class="col-7"><b>Totale</b></div>
              <div class="col-5 text-right">
                <h3 class="mb-0">
                  <span class="mr-1">€</span>
                  {{ numf(getCategoryTotal(cat)) }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <!-- buy total -->
        <div class="card mb-4 bp-bg-gray">
          <div class="card-body">
            <div class="row no-gutters">
              <div class="col-7"><h3 class="mb-0">Totale costi acquisto</h3></div>
              <div class="col-5 text-right">
                <h3 class="mb-0 text-primary">
                  <span class="mr-1">€</span>
                  {{ numf(getBuyTotal()) }}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- SELL -->
      <div class="col-md-6">
        <!-- sell categories -->
        <div v-for="cat in businessPlan.sell" :key="cat.key" class="card mb-4">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <h3 class="mb-0">{{ cat.label }}</h3>
              <button class="btn btn-xs btn-icon btn-ghost-primary" @click="toggleCat(cat.key)">
                <i v-if="!displayCat[cat.key]" class="tio-add"></i>
                <i v-else class="tio-remove"></i>
              </button>
            </div>

            <div v-show="displayCat[cat.key]">
              <!-- if units -->
              <div v-if="cat.units.length > 0" style="overflow-x: auto">
                <table class="table bp-table">
                  <thead class="thead-light">
                    <tr>
                      <th scope="col">UNIT&Agrave;</th>
                      <th scope="col">RICAVO UNITARIO</th>
                      <th scope="col">MQ</th>
                      <th scope="col">€/MQ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="unit in cat.units" :key="unit.id">
                      <th scope="row">
                        <div v-if="!isEditing">{{ unit.name }}</div>
                        <div v-else>
                          <select class="form-control bp-input" v-model="unit.name" @input="setUnitAsEdited(unit)">
                            <option v-for="(opt, i) in unitOptions" :key="i" :value="opt">{{ opt }}</option>
                          </select>
                        </div>
                      </th>
                      <td>
                        <div v-if="!isEditing"><span class="mr-1">€</span>{{ numf(unit.total) }}</div>
                        <div v-else class="d-flex">
                          <span class="mr-1">€</span>
                          <input type="number" class="form-control bp-input" v-model="unit.total" @input="setUnitAsEdited(unit)" />
                        </div>
                      </td>
                      <td>
                        <div v-if="!isEditing">{{ unit.mq }}</div>
                        <div v-else>
                          <input type="number" class="form-control bp-input" v-model="unit.mq" @input="setUnitAsEdited(unit)" />
                        </div>
                      </td>
                      <td><span class="mr-1">€</span>{{ numf(Number(unit.total) / Number(unit.mq)) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- if fields -->
              <div v-for="field in cat.fields" :key="field.id" class="row no-gutters py-1 border-bottom">
                <div class="col-7">{{ field.field.label }}</div>
                <div class="col-2 pr-3">
                  <div v-if="checkFieldComputedSecondary(field)" class="d-flex">
                    <span v-if="!isEditing">{{ numf(getFieldValue(field)) }}</span>
                    <input v-else type="number" class="form-control bp-input" v-model="field.custom_value" :placeholder="numf(getFieldValue(field))" @input="setFieldAsEdited(field)" />
                    <span v-if="field.field.computed.operator == '%'">%</span>
                  </div>
                </div>
                <div class="col-3">
                  <div class="d-flex justify-content-end">
                    <span class="mr-1">{{ field.field.unit }}</span>
                    <div v-if="checkFieldComputedSecondary(field)">
                      <span>{{ numf(getComputedSecondaryValue(field)) }}</span>
                    </div>
                    <div v-else>
                      <span v-if="!isEditing">{{ numf(getFieldValue(field)) }}</span>
                      <!-- <input v-else type="number" class="form-control bp-input" v-model="field.custom_value" :placeholder="numf(getFieldValue(field))" @input="setFieldAsEdited(field)" /> -->
                      <currency-input v-else short v-model="field.custom_value" :placeholder="numf(getFieldValue(field))" @input="setFieldAsEdited(field)" />
                    </div>
                    <button
                      type="button"
                      @click="field.custom_value = null"
                      data-toggle="tooltip"
                      data-html="true"
                      data-placement="left"
                      title="Ripristina valore di default"
                      class="btn btn-outline-secondary p-1 bp-reset-btn"
                      v-show="isEditing && field.custom_value != null"
                    >
                      <i class="tio-refresh"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="row no-gutters pt-3">
              <div class="col-7">
                <button v-if="cat.key == 'ricavi_vendita' && isEditing" class="btn btn-link" @click="addUnitToCategory(cat)">+ Aggiungi voce</button>
                <b v-else>Totale</b>
              </div>
              <div class="col-5 text-right">
                <h3 class="mb-0">
                  <span class="mr-1">€</span>
                  {{ numf(getCategoryTotal(cat)) }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <!-- sell margin -->
        <div class="card mb-4 bp-bg-gray">
          <div class="card-body">
            <div class="row no-gutters">
              <div class="col-7"><h3 class="mb-0">Margine commerciale</h3></div>
              <div class="col-5 text-right">
                <h3 class="mb-0 text-primary">
                  <span class="mr-1">€</span>
                  {{ numf(getSellMargin()) }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <!-- sell total -->
        <div class="card mb-4 bp-bg-gray">
          <div class="card-body">
            <div class="row no-gutters">
              <div class="col-7"><h3 class="mb-0">Utile lordo</h3></div>
              <div class="col-5 text-right">
                <h3 class="mb-0 text-primary">
                  <span class="mr-1">€</span>
                  {{ numf(getSellTotal()) }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <!-- months duration -->
        <div class="card mb-4 bp-bg-gray">
          <div class="card-body">
            <div class="row no-gutters">
              <div class="col-7"><h3 class="mb-0">Durata (mesi)</h3></div>
              <div class="col-5 text-right">
                <h3 v-if="!isEditing" class="mb-0 text-primary">{{ property.bp_months }}</h3>
                <input v-else type="number" class="form-control bp-input" v-model="property.bp_months" placeholder="durata in mesi" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { numf } from "@/helpers/helper";
import BpCalcs from "./bp-calcs";
let bpCalcs = null;

export default {
  data() {
    return {
      isSaving: false,
      isEditing: false,
      property: null,
      propertyBk: null,
      report: null,
      businessPlan: null,
      businessPlanBk: null,
      unitOptions: ["monolocale", "bilocale", "trilocale", "quadrilocale", "cantina", "garage", "varie"],
      displayCat: {}
    };
  },

  mounted() {
    this.property = this.$page.props.property;
    this.report = this.$page.props.report;
    this.businessPlan = this.property.business_plan;
    bpCalcs = new BpCalcs(this.businessPlan);

    for (let i = 0; i < this.businessPlan.buy.length; i++) {
      const cat = this.businessPlan.buy[i];
      this.displayCat[cat.key] = false;
    }

    for (let i = 0; i < this.businessPlan.sell.length; i++) {
      const cat = this.businessPlan.sell[i];
      this.displayCat[cat.key] = false;
    }
  },

  methods: {
    numf,

    getCategoryTotal: cat => bpCalcs.getCategoryTotal(cat),
    addUnitToCategory: cat => bpCalcs.addUnitToCategory(cat),
    getSellMargin: () => bpCalcs.getSellMargin(),
    getSellTotal: () => bpCalcs.getSellTotal(),
    getBuyTotal: () => bpCalcs.getBuyTotal(),
    setFieldAsEdited: field => bpCalcs.setFieldAsEdited(field),
    setUnitAsEdited: unit => bpCalcs.setUnitAsEdited(unit),
    getFieldValue: field => bpCalcs.getFieldValue(field),
    getComputedSecondaryValue: field => bpCalcs.getComputedSecondaryValue(field),
    checkFieldComputedSecondary: field => bpCalcs.checkFieldComputedSecondary(field),

    toggleCat(catKey) {
      this.displayCat[catKey] = !this.displayCat[catKey];
      this.displayCat = { ...this.displayCat };
    },

    edit() {
      this.propertyBk = _.cloneDeep(this.property);
      this.businessPlanBk = _.cloneDeep(this.businessPlan);
      this.isEditing = true;
      $('[data-toggle="tooltip"]').tooltip();
    },
    confirmSave() {
      this.$confirm({
        message: `Salvare il business plan?`,
        button: {
          no: "No",
          yes: "Si"
        },
        callback: confirm => {
          if (confirm) {
            this.save();
          }
        }
      });
    },
    async save() {
      this.isSaving = true;

      try {
        const response = await this.$inertia.post(`/admin/properties/${this.property.id}/business-plan`, {
          business_plan: this.businessPlan,
          bp_months: this.property.bp_months
        });
      } catch (error) {
        console.log(error);
      }

      this.isSaving = false;
      this.isEditing = false;
    },
    cancel() {
      this.property = _.cloneDeep(this.propertyBk);
      this.businessPlan = _.cloneDeep(this.businessPlanBk);
      this.isEditing = false;
    }
  }
};
</script>

<style lang="scss">
.bp-input {
  height: 1.5rem;
  padding: 0.2rem;
}
.bp-reset-btn {
  font-size: 0.8em;
  line-height: 0.5rem;
}
.bp-bg-gray {
  background-color: #f7f7f7;
}
</style>
